import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { featureSelectors } from '@lifesize/nucleus';
import { hooks, constants } from '@lifesize/clients.sdk';
import { VideoStream } from '@lifesize/clients.sdk/dist/components';
import { Box, makeStyles, Theme, Typography } from '@material-ui/core';
import { ReactComponent as WarningIcon } from '@lifesize/ux-assets/vectors/svgIcons/warning.svg';
import { colors } from '@lifesize/ux-assets';
import Button from 'components/Button/Button';
import DeviceMute, { muteType } from 'components/AvSettings/DeviceMute';
import { FLAG_VIRTUAL_BACKGROUND } from 'constants/FeatureFlags';
import { isWebGL2Supported } from 'utils/deviceUtils';
import { defaultVideoAspect } from 'utils/videoResolution';
import CameraDropdown from './CameraDropdown';
import ResolutionDropdown from './ResolutionDropdown';
import { RootState } from 'store/reducers';

interface Props {
  handleConfigChange: Function;
  intl: { formatMessage: Function };
  isInCall?: boolean;
  onToggleVbbSettings?: Function;
  isJoinFlowCall?: boolean;
}

export const VideoSettings = (props: Props) => {
  const { handleConfigChange, onToggleVbbSettings, isJoinFlowCall } = props;
  const classes = useStyles({});
  const mediaSettings = useSelector((state: RootState) => state?.app?.mediaSettings);
  const vbbFeatureEnabled = useSelector((state: RootState) =>
    featureSelectors.getFeatureFlag(state)(FLAG_VIRTUAL_BACKGROUND)
  );
  const localPrimaryStream = hooks.useLocalPrimaryStream();
  const isInCall = hooks.useCallState()?.state !== constants.CALL_STATE_DISCONNECTED;
  const [webGL2Supported] = useState(isWebGL2Supported());

  return (
    <section className={classes.container}>
      <CameraDropdown handleConfigChange={handleConfigChange} />
      <section>
        <Box className={classes.videoPreview}>
          <VideoStream
            elementId="vbbPreview"
            className={classes.videoMirror}
            muted={true}
            mediaStream={localPrimaryStream.stream}
          />
        </Box>
      </section>
      {vbbFeatureEnabled && onToggleVbbSettings && (
        <section>
          <div className={classes.btnContainer}>
            <Button
              className={classes.vbbButton}
              disabled={!webGL2Supported}
              id="selectVirtualBackground"
              kind="tertiary"
              onClick={onToggleVbbSettings}
            >
              <FormattedMessage
                id="components.avSettingsModal.vbbSettingsBtn"
                defaultMessage="Select Virtual Background"
              />
            </Button>
          </div>
          {!webGL2Supported && (
            <Typography variant={'h6'} className={classes.noGLWarning}>
              <WarningIcon />
              <div>
                <FormattedMessage
                  id="components.vbb.warning.webGL"
                  defaultMessage="Virtual Backgrounds require WebGL 2 which is either disabled or not available on your device"
                />
              </div>
            </Typography>
          )}
        </section>
      )}
      {!isInCall && (
        <section>
          <DeviceMute
            handleChange={() => {
              handleConfigChange('localVideoMuted', !mediaSettings.localVideoMuted);
            }}
            isInCall={isInCall}
            isJoinFlowCall={isJoinFlowCall}
            isMuted={mediaSettings.localVideoMuted}
            type={muteType.camera}
          />
        </section>
      )}
      <ResolutionDropdown handleConfigChange={handleConfigChange} />
    </section>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    '& > section': {
      display: 'flex',
      flexDirection: 'column',

      '&:not(:first-child)': {
        marginTop: theme.spacing(2)
      }
    }
  },
  heading: {
    fontSize: '1.6rem'
  },
  videoPreview: {
    flex: '0 0 auto',
    paddingTop: `calc(100% * ${defaultVideoAspect})`, // magic to maintain aspect ratio
    position: 'relative',
    width: '100%',
    '& video': {
      bottom: 0,
      height: '100%',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      width: '100%'
    }
  },
  videoMirror: {
    transform: 'rotateY(180deg)'
  },
  btnContainer: {
    width: '100%', // matches video

    '& button': {
      width: 'inherit'
    }
  },
  vbbButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  noGLWarning: {
    display: 'flex',
    fontSize: 12,
    marginTop: theme.spacing(1),
    '& svg': {
      color: colors.warningOrange,
      flex: '0 0 auto',
      height: 22,
      marginRight: theme.spacing(1),
      width: 'auto'
    },
    '& div': {
      // the message
      lineHeight: 1.4,
      paddingTop: '0.3em'
    }
  }
}));

export default injectIntl(VideoSettings);
